/**
 * Whether a preset's `member_count` is a real number or an API-suppressed 0.
 *
 * pymble-apis zeroes `member_count` (and nulls
 * `materialisation.last_member_count`) whenever the materialised
 * `Settings.ShowMemberList` is false, so that a caller cannot learn the size of
 * a hidden group by adding it to a preset and reading the count back. The
 * response never states the effective visibility directly.
 *
 * `show_member_list` is only the CONFIGURED OVERRIDE: `null` means none was
 * saved and the materialiser computed the effective value. Treating "not false"
 * as visible therefore prints a suppressed 0 as a genuine count - the exact
 * misreading a companion parent group makes dangerous, because "0 parents" for
 * a cohort that has families reads as a broken feature.
 *
 * With no override, `materialisation` settles it: a SUCCESSFUL run always
 * records `LastMemberCount` (`materialise.py` writes it alongside the status),
 * and the response nulls that field by the same rule it zeroes `member_count`.
 * So a success carrying no recorded count means both were suppressed. Before
 * any successful run there is nothing to infer from, and the honest answer is
 * "unknown" rather than a guess in either direction.
 *
 * Shared between the preset detail header and the companion section so the two
 * cannot disagree about the same number on the same page.
 */
import type { Preset } from "../../types/preset";

export type CountDisclosure = "visible" | "hidden" | "unknown";

export function memberCountDisclosure(preset: Preset): CountDisclosure {
  if (preset.show_member_list === true) return "visible";
  if (preset.show_member_list === false) return "hidden";
  const { last_run_status: status, last_member_count: recorded } =
    preset.materialisation;
  if (status !== "success") return "unknown";
  return (recorded ?? null) === null ? "hidden" : "visible";
}

/** Render a member count, or say why it is not a number. */
export function formatMemberCount(
  preset: Preset | null,
  loading: boolean
): string {
  if (loading) return "...";
  if (!preset) return "-";
  const disclosure = memberCountDisclosure(preset);
  if (disclosure === "hidden") return "hidden";
  if (disclosure === "unknown") return "not yet materialised";
  return String(preset.member_count);
}
